import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import { opportunityAttributeLvlElasticityExport } from 'gqlQueries/opportunityIdentification/attributeLevelElasticityQueries';
import { capitalizeFirstLetterOfEachWord } from 'utils/helperMethods';

const errorMessage: string = 'Something went wrong!';

interface IFetchAttributeLvlElasticityExportPayload {
    getAttributeLvlElasticityExport: string;
}

interface IGetAttributeLvlElasticity {
    attributeData: any[];
    count: number;
}

function useFetchAttributeLvlElasticityExport() {
    const [error, setError] = useState<any>(null);
    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    const [fetchAttributeLevelElasticityExport] = useLazyQuery(opportunityAttributeLvlElasticityExport, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
    });

    //called when fetching is complete
    function onFetchComplete(payload: IFetchAttributeLvlElasticityExportPayload) {
        try {
            const { getAttributeLvlElasticityExport } = payload;
            //if data present parse it
            if (getAttributeLvlElasticityExport) {
                const parsedData = parseExportData(getAttributeLvlElasticityExport);
                setData(parsedData);
                return;
            }
            setData(null);
        } catch (error: any) {
            setData(null);
            setError(error);
            console.error(errorMessage, error, 'logging error');
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        console.log('logging error', error);
        //sending empty data to error to avoid null data being sent
        setData(null);
        setLoading(false);
        setError(error?.message);
    }

    return {
        error,
        data,
        loading,
        fetchAttributeLevelElasticityExport: (variables: any) => {
            setLoading(true);
            fetchAttributeLevelElasticityExport(variables);
        },
    };
}

export { useFetchAttributeLvlElasticityExport };

const parseExportData = (rawInput: any) => {
    const parsedData = JSON.parse(rawInput);

    let result = [parsedData[0].map((i: any) => capitalizeFirstLetterOfEachWord(i, '_'))];
    // Update the 4th and 5th items' signs in the remaining rows
    const dataArray = parsedData.slice(1).map((row: any[]) => {
        if (row.length >= 5) {
            return [
                ...row.slice(0, 3),
                -row[3], // Negate 4th item
                -row[4], // Negate 5th item
                ...row.slice(5),
            ];
        }
        return row; // Return unchanged row if length is less than 5
    });

    result = [...result, ...dataArray];

    return result;
};
